import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { Box, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { CreateLabelDialog } from './CreateLabelDialog'
import { FALLBACK_LABEL_COLOR, useLabelColors } from './labelColors'

const Swatch = ({ color }: { color: string }): JSX.Element => (
  <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: color, flex: 'none' }} />
)

/**
 * The active-label chip plus its dropdown. Picking a label (or creating one)
 * assigns it to the currently selected cells and makes it the active label.
 */
export const LabelSelector = (): JSX.Element => {
  const {
    labeling: { activeLabel, sessionLabels },
    assignLabelToSelection,
  } = useLabelingSlice()
  const labelColors = useLabelColors()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const assignLabel = (label: string) => {
    assignLabelToSelection({ label, labeledAt: new Date().toISOString() })
  }

  const handlePick = (label: string) => {
    assignLabel(label)
    setAnchorEl(null)
  }

  return (
    <>
      <Box
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        data-testid="label-chip"
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Labeling:
        </Typography>
        {activeLabel ? <Swatch color={labelColors[activeLabel] ?? FALLBACK_LABEL_COLOR} /> : null}
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
          {activeLabel ?? 'Select a label'}
        </Typography>
        <ArrowDropDownIcon sx={{ color: 'primary.main' }} />
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {sessionLabels.map((label, index) => (
          <MenuItem key={label} onClick={() => handlePick(label)}>
            <Stack direction="row" alignItems="center" gap={1}>
              {index < 9 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', width: 12 }}>
                  {index + 1}
                </Typography>
              ) : (
                <Box sx={{ width: 12 }} />
              )}
              <Swatch color={labelColors[label] ?? FALLBACK_LABEL_COLOR} />
              <span>{label}</span>
            </Stack>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            setCreateOpen(true)
          }}
          sx={{ color: 'primary.main' }}
        >
          + create a new label
        </MenuItem>
      </Menu>

      <CreateLabelDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={(label) => assignLabel(label)}
      />
    </>
  )
}

export default LabelSelector
